/**
 * Browser {@link FileStore}.
 *
 * Backed by the Origin Private File System when the browser has it — working
 * copies and recovery points then survive a tab crash, which is what §23 asks
 * for — and by memory when it does not. The fallback is stated rather than
 * hidden: `durable` says which one is in use, and the UI tells the user when
 * recovery will not survive a reload.
 *
 * Save As goes through the File System Access API where available and falls
 * back to a download, because a browser cannot silently write to disk and
 * pretending otherwise would be the kind of claim §19 rules out.
 */
import { FileStoreError } from '@moduviewer/core';
import type {
  FileStat, FileStore, OriginalRef, RecoveryPoint, SaveOutcome, WorkingCopyRef,
} from '@moduviewer/core';

const WORK_DIR = 'work';
const RECOVERY_DIR = 'recovery';

/**
 * `FileSystemWritableFileStream.write` will not take a `Uint8Array` whose
 * backing store might be shared, and TypeScript cannot prove ours is not. A
 * fresh `ArrayBuffer` is both provably exclusive and what the API wants.
 */
function toExclusiveBuffer(bytes: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

export interface BrowserFileStoreOptions {
  readonly now?: () => number;
}

export class BrowserFileStore implements FileStore {
  /** Files handed in by the picker or a drop, keyed by handle. */
  readonly #incoming = new Map<string, File>();
  readonly #memory = new Map<string, Uint8Array>();
  readonly #recovery = new Map<string, RecoveryPoint>();
  readonly #now: () => number;
  #root: FileSystemDirectoryHandle | null = null;
  #counter = 0;

  constructor(options: BrowserFileStoreOptions = {}) {
    this.#now = options.now ?? (() => Date.now());
  }

  /** True when working copies survive a reload. */
  get durable(): boolean {
    return this.#root !== null;
  }

  async init(): Promise<void> {
    const nav = navigator as Navigator & { storage?: { getDirectory?: () => Promise<FileSystemDirectoryHandle> } };
    if (!nav.storage?.getDirectory) return;
    try {
      this.#root = await nav.storage.getDirectory();
    } catch {
      this.#root = null;
    }
  }

  /** Register a `File` from a picker or a drop and return its handle. */
  register(file: File): string {
    const handle = `file://${++this.#counter}/${file.name}`;
    this.#incoming.set(handle, file);
    return handle;
  }

  /**
   * Register bytes the app produced rather than received — an entry extracted
   * from an archive, say. Opening one has to go through the same pipeline as
   * any other file, detection and security assessment included, and that
   * pipeline works on handles (§21: an entry is not trusted for being inside
   * an archive we already opened).
   */
  async stageBytes(bytes: Uint8Array, name: string): Promise<string> {
    return this.register(new File([bytes as BlobPart], name));
  }

  async stat(handle: string): Promise<FileStat> {
    const file = this.#incoming.get(handle);
    if (file) return { sizeBytes: file.size, modifiedAtMs: file.lastModified, exists: true };
    const bytes = this.#memory.get(handle);
    if (bytes) return { sizeBytes: bytes.length, modifiedAtMs: this.#now(), exists: true };
    const opfs = await this.#readOpfs(handle);
    return opfs
      ? { sizeBytes: opfs.length, modifiedAtMs: this.#now(), exists: true }
      : { sizeBytes: 0, modifiedAtMs: 0, exists: false };
  }

  async readRange(handle: string, start: number, end: number): Promise<Uint8Array> {
    const file = this.#incoming.get(handle);
    if (file) {
      // A real range read: the browser does not load the whole file to slice it,
      // which is what makes §33's head-only identification cheap here too.
      return new Uint8Array(await file.slice(start, end).arrayBuffer());
    }
    const all = await this.readAll(handle);
    return all.slice(start, end);
  }

  async readAll(handle: string): Promise<Uint8Array> {
    const file = this.#incoming.get(handle);
    if (file) return new Uint8Array(await file.arrayBuffer());
    const bytes = this.#memory.get(handle);
    if (bytes) return Uint8Array.from(bytes);
    const opfs = await this.#readOpfs(handle);
    if (opfs) return opfs;
    throw new FileStoreError('io-error', `no such handle: ${handle}`);
  }

  /* ---- working copy ---- */

  async createWorkingCopy(original: OriginalRef, sessionId: string): Promise<WorkingCopyRef> {
    void original;
    const name = `${sessionId}-${crypto.randomUUID().slice(0, 8)}.part`;
    return { handle: `${WORK_DIR}/${name}`, createdAtMs: this.#now() };
  }

  async writeWorkingCopy(ref: WorkingCopyRef, bytes: Uint8Array): Promise<void> {
    await this.#write(ref.handle, bytes);
  }

  async readWorkingCopy(ref: WorkingCopyRef): Promise<Uint8Array> {
    return this.readAll(ref.handle);
  }

  async releaseWorkingCopy(ref: WorkingCopyRef): Promise<void> {
    await this.#remove(ref.handle);
  }

  /* ---- recovery ---- */

  async writeRecoveryPoint(
    sessionId: string, revision: number, bytes: Uint8Array, atMs: number,
  ): Promise<RecoveryPoint> {
    const name = `${String(revision).padStart(8, '0')}-${atMs}.part`;
    const handle = `${RECOVERY_DIR}/${sessionId}/${name}`;
    await this.#write(handle, bytes);
    const point: RecoveryPoint = { id: `${sessionId}:${name}`, handle, createdAtMs: atMs, revision };
    this.#recovery.set(point.id, point);
    return point;
  }

  async listRecoveryPoints(sessionId?: string): Promise<RecoveryPoint[]> {
    const known = [...this.#recovery.values()];
    if (this.#root) {
      // After a reload the in-memory index is empty but the files are not, so
      // the directory is the source of truth for what can be recovered.
      try {
        const dir = await this.#root.getDirectoryHandle(RECOVERY_DIR, { create: true });
        for await (const session of dir.keys()) {
          if (sessionId && session !== sessionId) continue;
          const sub = await dir.getDirectoryHandle(session, { create: false });
          for await (const name of sub.keys()) {
            const id = `${session}:${name}`;
            if (this.#recovery.has(id)) continue;
            const [revisionPart, atPart] = name.replace(/\.part$/, '').split('-');
            known.push({
              id,
              handle: `${RECOVERY_DIR}/${session}/${name}`,
              createdAtMs: Number(atPart) || 0,
              revision: Number(revisionPart) || 0,
            });
          }
        }
      } catch { /* directory missing: nothing to recover */ }
    }
    return known
      .filter((p) => !sessionId || p.id.startsWith(`${sessionId}:`))
      .sort((a, b) => a.createdAtMs - b.createdAtMs);
  }

  async readRecoveryPoint(point: RecoveryPoint): Promise<Uint8Array> {
    return this.readAll(point.handle);
  }

  async deleteRecoveryPoints(points: readonly RecoveryPoint[]): Promise<void> {
    for (const point of points) {
      await this.#remove(point.handle);
      this.#recovery.delete(point.id);
    }
  }

  /* ---- saving ---- */

  async exists(): Promise<boolean> {
    // The browser hands naming to the OS dialog, which does its own overwrite
    // prompt. Claiming to know what is on the user's disk would be a lie.
    return false;
  }

  async saveAs(bytes: Uint8Array, suggestedName: string, revision: number): Promise<SaveOutcome> {
    const picker = (window as unknown as {
      showSaveFilePicker?: (o: { suggestedName: string }) => Promise<{
        createWritable(): Promise<{ write(d: ArrayBuffer): Promise<void>; close(): Promise<void> }>;
        name: string;
      }>;
    }).showSaveFilePicker;

    try {
      if (picker) {
        const handle = await picker({ suggestedName });
        const writable = await handle.createWritable();
        await writable.write(toExclusiveBuffer(bytes));
        await writable.close();
        return {
          ok: true,
          ref: {
            handle: `saved://${handle.name}`, displayName: handle.name,
            savedAtMs: this.#now(), revision, replacedOriginal: false, validated: false,
          },
        };
      }

      // Fallback: a download. The file lands wherever the browser puts
      // downloads, and the UI says so rather than implying we chose.
      const blob = new Blob([toExclusiveBuffer(bytes)]);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = suggestedName;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return {
        ok: true,
        ref: {
          handle: `download://${suggestedName}`, displayName: suggestedName,
          savedAtMs: this.#now(), revision, replacedOriginal: false, validated: false,
        },
      };
    } catch (err) {
      const message = String((err as Error)?.name ?? '');
      return { ok: false, reason: message === 'AbortError' ? 'cancelled' : 'io-error' };
    }
  }

  async overwriteOriginal(
    original: OriginalRef, bytes: Uint8Array, revision: number,
  ): Promise<SaveOutcome> {
    // A file that arrived through a drop or an `<input>` carries no write
    // permission. §19 forbids pretending otherwise, so this is refused rather
    // than silently turned into a Save As.
    void original; void bytes; void revision;
    return { ok: false, reason: 'permission-denied' };
  }

  async sweepCache(olderThanMs: number, nowMs: number): Promise<number> {
    let removed = 0;
    for (const point of await this.listRecoveryPoints()) {
      if (nowMs - point.createdAtMs > olderThanMs) {
        await this.#remove(point.handle);
        this.#recovery.delete(point.id);
        removed++;
      }
    }
    return removed;
  }

  /* ---- storage helpers ---- */

  async #write(handle: string, bytes: Uint8Array): Promise<void> {
    if (!this.#root) { this.#memory.set(handle, Uint8Array.from(bytes)); return; }
    try {
      const file = await this.#resolve(handle, true);
      const writable = await file.createWritable();
      await writable.write(toExclusiveBuffer(bytes));
      await writable.close();
    } catch (err) {
      throw new FileStoreError('io-error', String((err as Error).message));
    }
  }

  async #readOpfs(handle: string): Promise<Uint8Array | null> {
    if (!this.#root) return null;
    try {
      const file = await this.#resolve(handle, false);
      return new Uint8Array(await (await file.getFile()).arrayBuffer());
    } catch {
      return null;
    }
  }

  async #remove(handle: string): Promise<void> {
    this.#memory.delete(handle);
    if (!this.#root) return;
    const parts = handle.split('/');
    const name = parts.pop()!;
    try {
      let dir = this.#root;
      for (const part of parts) dir = await dir.getDirectoryHandle(part, { create: false });
      await dir.removeEntry(name);
    } catch { /* already gone */ }
  }

  async #resolve(handle: string, create: boolean): Promise<FileSystemFileHandle> {
    const parts = handle.split('/');
    const name = parts.pop()!;
    let dir = this.#root!;
    for (const part of parts) dir = await dir.getDirectoryHandle(part, { create });
    return dir.getFileHandle(name, { create });
  }
}
